// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Pressable, StyleSheet, View } from 'react-native';

import GoffyInput from '@/components/GoffyInput';
import { Text } from '@/components/Themed';
import { useI18n } from '@/i18n';
import { MAX_TOUR_NAME_LENGTH, isValidTourName } from '@/services/tourName';
import appColors from '@/theme/colors';

type Props = {
  visible: boolean;
  creating: boolean;
  error: string | null;
  onCreate: (name: string) => void;
  onCancel: () => void;
};

/**
 * Asks for a name before the tour exists. Creation used to fire on the first
 * tap with a default name, which left a permanent tour behind every abandoned
 * attempt and made every tour share a name.
 */
export default function CreateTourModal({ visible, creating, error, onCreate, onCancel }: Props) {
  const { t } = useI18n();
  const [name, setName] = useState('');

  // Start each attempt clean so a cancelled name never leaks into the next one.
  useEffect(() => {
    if (visible) setName('');
  }, [visible]);

  const canCreate = isValidTourName(name) && !creating;

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onCancel}>
      <Pressable style={styles.overlay} onPress={creating ? undefined : onCancel}>
        <Pressable style={styles.card} onPress={() => null}>
          <Text style={styles.title}>{t('tournaments.createTitle')}</Text>
          <Text style={styles.label}>{t('tournaments.nameLabel')}</Text>
          <GoffyInput
            style={styles.input}
            value={name}
            onChangeText={setName}
            placeholder={t('tournaments.namePlaceholder')}
            maxLength={MAX_TOUR_NAME_LENGTH}
            autoFocus
            editable={!creating}
            returnKeyType="done"
            onSubmitEditing={() => { if (canCreate) onCreate(name); }}
          />
          {error ? <Text style={styles.errorText}>{error}</Text> : null}
          <View style={styles.actions}>
            <Pressable
              style={[styles.cancelButton, creating && styles.disabled]}
              onPress={onCancel}
              disabled={creating}
            >
              <Text style={styles.cancelText}>{t('tournaments.createCancel')}</Text>
            </Pressable>
            <Pressable
              style={[styles.confirmButton, !canCreate && styles.disabled]}
              onPress={() => onCreate(name)}
              disabled={!canCreate}
            >
              {creating ? (
                <ActivityIndicator size="small" color={appColors.text.inverse} />
              ) : (
                <Text style={styles.confirmText}>{t('tournaments.createConfirm')}</Text>
              )}
            </Pressable>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(17,44,27,0.22)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  card: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    backgroundColor: '#07200D',
    padding: 18,
    gap: 12,
  },
  title: {
    color: appColors.text.primary,
    fontSize: 22,
    fontWeight: '800',
    textAlign: 'center',
  },
  label: {
    color: appColors.text.muted,
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  // Frame and fill come from GoffyInput; the tour name is deliberately larger and bolder than a
  // default field.
  input: {
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    fontWeight: '600',
  },
  errorText: {
    color: appColors.status.danger,
    fontSize: 13,
    fontWeight: '600',
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelButton: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: appColors.accent.subtleBorder,
    backgroundColor: appColors.accent.subtleBg,
  },
  cancelText: {
    color: appColors.text.primary,
    fontWeight: '700',
    fontSize: 13,
  },
  confirmButton: {
    minWidth: 96,
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 999,
    backgroundColor: appColors.accent.primary,
  },
  confirmText: {
    color: appColors.text.inverse,
    fontWeight: '800',
    fontSize: 13,
  },
  disabled: {
    opacity: 0.5,
  },
});
